import { useState, FormEvent, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import Select from "react-select";
import { addBudgetRequest, updateBudgetRequest, getArticles } from "api/budget_request";
import moment from "moment";
import { ArticlesResponse, BudgetTableItem, emptyBudgetRow } from "types/budget";
import ActionButton from "components/table/action-btn";
import { IconInfoCircle, IconMinus } from "@tabler/icons-react";
import FormattedNumberInput from "components/input/formatted-number";
import { LevelDefault } from "types/dict";
import DangerModal from "components/modal/danger";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  budget_type_id: number;
  year: number;
  company_id: number;
  department_id: number;
};

type SelectOption = {
  value: number;
  label: string;
  disabled?: boolean;
};

const REQUIRED_FIELDS = ["budget_type_id", "year", "company_id", "department_id"] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

const VAT_OPTIONS = [
  {
    value: "true",
    label: "Да",
    disabled: false,
  },
  {
    value: "false",
    label: "Нет",
    disabled: false,
  },
];

export default function NewBudget() {
  const {
    departments,
    getDepartmentsData,
    companies,
    getCompaniesData,
    contractors,
    getContractorsData,
    levels,
    getLevelsData,
    codes,
    getCodesData,
    budgetRequests,
    isAdmin,
    getBudgetRequestsData,
    searchQuery,
    handleSearch,
    notify,
  } = useSharedState();
  const { budgetId } = useParams();
  const navigate = useNavigate();

  const [formRecords, setFormRecords] = useState<FormRecords>({
    budget_type_id: 0,
    year: moment().year(),
    company_id: 0,
    department_id: 0,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [requestTable, setRequestTable] = useState<BudgetTableItem[]>([emptyBudgetRow]);

  const [articleOptions, setArticleOptions] = useState<{ label: string; value: string }[][]>([]);
  const [articleLoading, setArticleLoading] = useState<boolean[]>([]);
  const [articleInput, setArticleInput] = useState<string[]>([]);
  const articleTimeouts = useRef<(number | null)[]>([]);

  const deleteBudgetDanger = "Вы действительно хотите удалить бюджет?";

  const openBudgetModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const zeroLevels = useMemo(() => {
    return levels.filter((lvl) => lvl.parent_budget_type_id === null);
  }, [levels]);

  const mainLevel = useMemo(() => {
    return zeroLevels.find((lvl) => lvl.budget_type_id === formRecords.budget_type_id);
  }, [zeroLevels, formRecords.budget_type_id]);

  const chosenLevels = useMemo(() => {
    return levels.filter((lvl) => lvl.parent_budget_type_id === mainLevel?.budget_type_id);
  }, [levels, mainLevel]);

  const codeOptions = useMemo(() => {
    return codes.map((lvl) => ({
      value: lvl.code_it_id,
      label: lvl.code_it,
      disabled: false,
    }));
  }, [codes]);

  const contractorOptions = useMemo(() => {
    return contractors.map((lvl) => ({
      value: lvl.contragent_id,
      label: lvl.contragent,
      disabled: false,
    }));
  }, [contractors]);

  const totalSum = useMemo(() => {
    return requestTable.reduce((sum, item) => sum + (item.row_total || 0), 0);
  }, [requestTable]);

  useEffect(() => {
    getDepartmentsData();
    getCompaniesData();
    getContractorsData();
    getLevelsData();
    getCodesData();
  }, []);

  useEffect(() => {
    if (budgetId) {
      const budget = budgetRequests.budget_requests.find((t) => t.budget_request_id === Number(budgetId));
      if (budget) {
        setFormRecords({
          budget_type_id: zeroLevels.find((lvl) => lvl.budget_type === budget.budget_type)?.budget_type_id || 0,
          year: budget.year,
          company_id: companies.find((c) => c.company === budget.company)?.company_id || 0,
          department_id: departments.find((c) => c.department === budget.department)?.department_id || 0,
        });
        setRequestTable(
          budget.budget_request_details.map((detail) => ({
            ...detail,
            budget_line_rows: detail.budget_line_rows.map(
              (row) => levels.find((lvl) => lvl.budget_type_id === row.budget_type_id) || LevelDefault
            ),
          }))
        );
      } else {
        notify("Бюджет не найден", "danger");
        navigate("/budgets");
      }
    }
  }, [budgetId, budgetRequests, zeroLevels, companies, departments, levels]);

  const budgetFields = [
    {
      id: "budget_type_id",
      title: "Название бюджета",
      type: "select",
      placeholder: "Выберите бюджет",
      options: zeroLevels.map((lvl) => ({
        label: lvl.budget_type!,
        value: lvl.budget_type_id!,
        disabled: !!!lvl.status,
      })),
    },
    {
      id: "year",
      title: "Год",
      type: "number",
      placeholder: undefined,
      options: undefined,
    },
    {
      id: "company_id",
      title: "Юр. лицо",
      type: "select",
      placeholder: "Выберите юр. лицо",
      options: companies
        .filter((com) => com.status)
        .map((com) => ({
          label: com.company,
          value: com.company_id,
        })),
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: "Выберите отдел",
      options: departments
        .filter((dep) => dep.status)
        .map((dep) => ({
          label: dep.department,
          value: dep.department_id,
        })),
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value || !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRequestTable = () => {
    return requestTable.every((row) => {
      const hasRequiredFields =
        row.article &&
        row.code_it_id &&
        row.contragent_id &&
        row.quantity !== null &&
        row.quantity !== undefined &&
        row.mon_1 !== null &&
        row.mon_1 !== undefined &&
        row.mon_2 !== null &&
        row.mon_2 !== undefined &&
        row.mon_3 !== null &&
        row.mon_3 !== undefined &&
        row.mon_4 !== null &&
        row.mon_4 !== undefined &&
        row.mon_5 !== null &&
        row.mon_5 !== undefined &&
        row.mon_6 !== null &&
        row.mon_6 !== undefined &&
        row.mon_7 !== null &&
        row.mon_7 !== undefined &&
        row.mon_8 !== null &&
        row.mon_8 !== undefined &&
        row.mon_9 !== null &&
        row.mon_9 !== undefined &&
        row.mon_10 !== null &&
        row.mon_10 !== undefined &&
        row.mon_11 !== null &&
        row.mon_11 !== undefined &&
        row.mon_12 !== null &&
        row.mon_12 !== undefined;
      const hasAllLevels = mainLevel?.max_levels ? row.budget_line_rows?.length === mainLevel.max_levels : true;
      return hasRequiredFields && hasAllLevels;
    });
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SelectChangeEvent = readonly SelectOption[] | null;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SelectChangeEvent | SingleSelectEvent, fieldId?: string) => {
    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      setFormRecords((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      // Handle select change
      // @ts-ignore
      const values = Array.isArray(e) ? e.map((option) => option.value) : e ? e.value : "";
      setFormRecords((prev) => ({ ...prev, [fieldId!]: values }));
      setErrors((prev) => ({ ...prev, [fieldId!]: false }));
    }
  };

  const updateTable = async () => {
    await getBudgetRequestsData();
    await handleSearch(searchQuery, undefined);
  };

  const submitBudget = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !validateRequestTable()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      if (budgetId) {
        await updateBudgetRequest(Number(budgetId), {
          ...formRecords,
          budget_request_details: requestTable.map((row) => ({
            ...row,
            contragent_id: row.contragent_id || undefined,
          })),
        });
        notify("Бюджет обновлен", "success");
      } else {
        await addBudgetRequest({
          ...formRecords,
          budget_request_details: requestTable.map((row) => ({
            ...row,
            contragent_id: row.contragent_id || undefined,
          })),
        });
        notify("Бюджет создан", "success");
      }

      setFormSubmitted(true);
      await updateTable();
      const currentURL = new URL(window.location.href);
      currentURL.pathname = "/budgets";
      window.history.replaceState({}, "", currentURL.toString());
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", budgetId ? "Ошибка при обновлении бюджета" : "Ошибка при создании бюджета");
    } finally {
      setFormLoading(false);
    }
  };

  const handleBudgetDelete = async () => {
    openBudgetModal();
    setFormLoading(true);

    try {
      const budget = { ...budgetRequests.budget_requests.find((t) => t.budget_request_id === Number(budgetId)) };
      const payload = {
        ...budget,
        budget_type_id: budget.budget_request_id,
        company_id: companies.find((c) => c.company === budget.company)?.company_id || 0,
        department_id: departments.find((c) => c.department === budget.department)?.department_id || 0,
        status: false,
      };
      await updateBudgetRequest(Number(budgetId), payload);

      notify("Бюджет удалён", "success");
      setFormSubmitted(true);
      await updateTable();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении бюджета");
    }
  };

  const handleAddRow = () => {
    setRequestTable((prev) => [...prev, { ...emptyBudgetRow }]);
  };

  const handleDeleteRow = (index: number) => {
    setRequestTable((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle article search and debounce
  const handleArticleInputChange = (inputValue: string, index: number) => {
    setArticleInput((prev) => {
      const arr = [...prev];
      arr[index] = inputValue;
      return arr;
    });
    if (articleTimeouts.current[index]) {
      clearTimeout(articleTimeouts.current[index]!);
    }
    if (inputValue.trim().length >= 3) {
      setArticleLoading((prev) => {
        const arr = [...prev];
        arr[index] = true;
        return arr;
      });
      articleTimeouts.current[index] = window.setTimeout(async () => {
        try {
          const res: ArticlesResponse = await getArticles(inputValue.trim());
          setArticleOptions((prev) => {
            const arr = [...prev];
            arr[index] = Array.isArray(res.articles) ? res.articles.map((a) => ({ label: a, value: a })) : [];
            return arr;
          });
        } catch {
          setArticleOptions((prev) => {
            const arr = [...prev];
            arr[index] = [];
            return arr;
          });
        } finally {
          setArticleLoading((prev) => {
            const arr = [...prev];
            arr[index] = false;
            return arr;
          });
        }
      }, 1000);
    } else {
      setArticleOptions((prev) => {
        const arr = [...prev];
        arr[index] = [];
        return arr;
      });
      setArticleLoading((prev) => {
        const arr = [...prev];
        arr[index] = false;
        return arr;
      });
    }
  };

  const handleTableChange = (
    index: number,
    field: keyof BudgetTableItem,
    value: string | number | boolean,
    lvl_num?: number
  ) => {
    setRequestTable((prev) => {
      const newTable = [...prev];
      newTable[index] = {
        ...newTable[index],
        [field]:
          field === "quantity" || field === "code_it_id" || field === "contragent_id"
            ? value === ""
              ? undefined
              : Number(value)
            : field === "vat"
            ? value === ""
              ? false
              : Boolean(value === "true" ? true : false)
            : field === "budget_line_rows"
            ? (() => {
                const newValue = chosenLevels.find((lvl) => lvl.budget_type === value && lvl.level_number === lvl_num);
                if (!newValue) return newTable[index].budget_line_rows || [];

                const existingRows = [...(newTable[index].budget_line_rows || [])];
                existingRows[newValue.level_number! - 1] = newValue;
                return existingRows;
              })()
            : field === "article"
            ? value
            : value === ""
            ? undefined
            : Number(value),
      };

      // Recalculate row total if any month value changes
      if (field.startsWith("mon_")) {
        const months = Array.from({ length: 12 }, (_, i) => `mon_${i + 1}` as keyof BudgetTableItem);
        newTable[index].row_total = months.reduce((sum, month) => sum + ((newTable[index][month] as number) || 0), 0);
      }

      return newTable;
    });
  };

  const requestTableHeaders = [
    {
      header:
        !isAdmin && budgetId ? undefined : (
          <button type="button" className="btn btn-ghost-primary" onClick={handleAddRow}>
            Добавить строку
          </button>
        ),
      required: false,
      narrow: true,
      wrap: false,
    },
    { header: "Наименование статьи", required: true, narrow: false, wrap: false },
    ...(mainLevel?.max_levels && mainLevel?.max_levels > 0
      ? Array.from({ length: mainLevel.max_levels }, (_, i) => ({
          header: `Уровень ${i + 1}`,
          required: true,
          narrow: false,
          wrap: false,
        }))
      : []),
    { header: "Код ИТ", required: true, narrow: false, wrap: true },
    { header: "Контрагент", required: true, narrow: false, wrap: true },
    { header: "НДС", required: true, narrow: false, wrap: false },
    { header: "Количество", required: true, narrow: false, wrap: false },
    ...[
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ].map((month) => ({ header: month, required: true, narrow: false, wrap: false })),
    { header: "Сумма", required: false, narrow: false, wrap: false },
  ];

  const renderField = ({ id, title, type, placeholder, options }: (typeof budgetFields)[number]) => {
    return (
      <div className={"col-lg-6"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "number" ? (
              <input
                type="number"
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                min={1990}
                step={1}
                value={formRecords[id]}
                onChange={handleInputChange}
                disabled={isFormLoading || !!budgetId}
              />
            ) : (
              <Select<SelectOption>
                name={id}
                placeholder={placeholder}
                value={options?.find((opt) => opt.value === formRecords[id])}
                onChange={(newValue) => handleInputChange(newValue, id)}
                isDisabled={isFormLoading || !!budgetId}
                options={options}
                isOptionDisabled={(option) => option.disabled || false}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            )}

            <div className="invalid-feedback">{getInputErrorMessage(id)}</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newBudgetBody = (
    <div>
      <form id="budgetForm" className="row" onSubmit={submitBudget}>
        {budgetFields.map(renderField)}

        <div className="col-12 mb-3">
          <div className="d-flex gap-3">
            <label className="form-label required">Таблица бюджетов</label>
            <div className="alert alert-info px-2 py-0" role="alert">
              <div className="text-secondary">Если данные для заполняемых ячеек отстутствуют - впишите в них "0"</div>
            </div>
          </div>
          <div className="card">
            <div className="table-responsive" style={{ maxHeight: 400 }}>
              <table className="table table-vcenter card-table">
                <thead style={{ position: "sticky", zIndex: 1, top: 0, background: "white" }}>
                  <tr>
                    {requestTableHeaders.map((header, index) => (
                      <th
                        className={`${header.narrow && "w-1"} ${header.wrap && "text-wrap"} ${
                          header.required && "header-required"
                        }`}
                        key={index}
                      >
                        {header.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requestTable?.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div
                          className={`align-items-center justify-content-center gap-3 ${
                            !isAdmin && budgetId ? "" : "d-flex"
                          }`}
                        >
                          {index + 1}
                          {!isAdmin && budgetId ? undefined : (
                            <div className="btn-list flex-nowrap">
                              <ActionButton
                                icon={<IconMinus className="icon" />}
                                title="Удалить строку"
                                action={() => handleDeleteRow(index)}
                                color={"danger"}
                                modal={false}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="Введите наименование статьи"
                          value={item.article ? { label: item.article, value: item.article } : null}
                          onInputChange={(inputValue) => {
                            handleArticleInputChange(inputValue, index);
                            return inputValue;
                          }}
                          onChange={(newValue) => handleTableChange(index, "article", newValue ? newValue.value : "")}
                          options={
                            articleInput[index]?.trim().length >= 3 &&
                            articleOptions[index]?.length === 0 &&
                            !articleLoading[index]
                              ? [
                                  {
                                    label: `Сохранить: \"${articleInput[index]}\"`,
                                    value: articleInput[index],
                                  },
                                ]
                              : articleOptions[index]
                          }
                          isLoading={articleLoading[index]}
                          isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            container: (base) => ({ ...base, width: 200 }),
                          }}
                          inputValue={articleInput[index]}
                          onBlur={() => {
                            // If user leaves field and nothing is selected, keep their input as value
                            if (
                              articleInput[index] &&
                              (!item.article || item.article !== articleInput[index]) &&
                              (articleOptions[index]?.length === 0 ||
                                !articleOptions[index]?.some((opt) => opt.value === articleInput[index]))
                            ) {
                              handleTableChange(index, "article", articleInput[index]);
                            }
                          }}
                        />
                      </td>

                      {(mainLevel?.max_levels && mainLevel?.max_levels > 0
                        ? Array.from({ length: mainLevel.max_levels }, (_, i) => ({ num: i + 1 }))
                        : []
                      ).map((lvl, idx) => (
                        <td key={idx}>
                          <div style={{ width: 200 }}>
                            <Select
                              classNamePrefix="react-select"
                              placeholder="Выберите уровень"
                              value={
                                chosenLevels
                                  .filter((lv) => lv.level_number === lvl.num)
                                  .map((opt) => ({
                                    value: opt.budget_type,
                                    label: opt.budget_type,
                                    disabled: !!!opt.status,
                                  }))
                                  .find(
                                    (opt) => opt.value === requestTable[index].budget_line_rows[idx]?.budget_type
                                  ) || null
                              }
                              onChange={(newValue) =>
                                handleTableChange(index, "budget_line_rows", newValue ? newValue.value! : "", lvl.num)
                              }
                              options={chosenLevels
                                .filter((lv) => lv.level_number === lvl.num)
                                .map((opt) => ({
                                  value: opt.budget_type,
                                  label: opt.budget_type,
                                  disabled: !!!opt.status,
                                }))}
                              isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                              isOptionDisabled={(opt) => opt.disabled}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              }}
                            />
                          </div>
                        </td>
                      ))}

                      <td>
                        <div style={{ width: 200 }}>
                          <Select
                            classNamePrefix="react-select"
                            name="code_it_id"
                            placeholder="Выберите код"
                            value={codeOptions.find((opt) => opt.value === requestTable[index].code_it_id)}
                            onChange={(newValue) =>
                              handleTableChange(index, "code_it_id", newValue ? newValue.value : "")
                            }
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            options={codeOptions}
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div style={{ width: 210 }}>
                          <Select
                            classNamePrefix="react-select"
                            name="contragent_id"
                            placeholder="Выберите контрагента"
                            value={contractorOptions.find((opt) => opt.value === requestTable[index].contragent_id)}
                            onChange={(newValue) =>
                              handleTableChange(index, "contragent_id", newValue ? newValue.value : "")
                            }
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            options={contractorOptions}
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div style={{ width: 200 }}>
                          <Select
                            classNamePrefix="react-select"
                            name="vat"
                            placeholder="Выберите вариант"
                            value={VAT_OPTIONS.find((opt) => String(opt.value) === String(requestTable[index].vat))}
                            onChange={(newValue) => handleTableChange(index, "vat", newValue ? newValue.value : "")}
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            options={VAT_OPTIONS}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 100 }}
                          name="quantity"
                          value={item.quantity !== null && item.quantity !== undefined ? item.quantity : null}
                          onChange={(e) => handleTableChange(index, "quantity", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_1"
                          value={item.mon_1 !== null && item.mon_1 !== undefined ? item.mon_1 : null}
                          onChange={(e) => handleTableChange(index, "mon_1", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_2"
                          value={item.mon_2 !== null && item.mon_2 !== undefined ? item.mon_2 : null}
                          onChange={(e) => handleTableChange(index, "mon_2", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_3"
                          value={item.mon_3 !== null && item.mon_3 !== undefined ? item.mon_3 : null}
                          onChange={(e) => handleTableChange(index, "mon_3", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_4"
                          value={item.mon_4 !== null && item.mon_4 !== undefined ? item.mon_4 : null}
                          onChange={(e) => handleTableChange(index, "mon_4", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_5"
                          value={item.mon_5 !== null && item.mon_5 !== undefined ? item.mon_5 : null}
                          onChange={(e) => handleTableChange(index, "mon_5", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_6"
                          value={item.mon_6 !== null && item.mon_6 !== undefined ? item.mon_6 : null}
                          onChange={(e) => handleTableChange(index, "mon_6", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_7"
                          value={item.mon_7 !== null && item.mon_7 !== undefined ? item.mon_7 : null}
                          onChange={(e) => handleTableChange(index, "mon_7", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_8"
                          value={item.mon_8 !== null && item.mon_8 !== undefined ? item.mon_8 : null}
                          onChange={(e) => handleTableChange(index, "mon_8", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_9"
                          value={item.mon_9 !== null && item.mon_9 !== undefined ? item.mon_9 : null}
                          onChange={(e) => handleTableChange(index, "mon_9", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_10"
                          value={item.mon_10 !== null && item.mon_10 !== undefined ? item.mon_10 : null}
                          onChange={(e) => handleTableChange(index, "mon_10", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_11"
                          value={item.mon_11 !== null && item.mon_11 !== undefined ? item.mon_11 : null}
                          onChange={(e) => handleTableChange(index, "mon_11", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="mon_12"
                          value={item.mon_12 !== null && item.mon_12 !== undefined ? item.mon_12 : null}
                          onChange={(e) => handleTableChange(index, "mon_12", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 150 }}
                          name="row_total"
                          value={item.row_total !== null && item.row_total !== undefined ? item.row_total : null}
                          onChange={(e) => handleTableChange(index, "row_total", e.target.value)}
                          disabled
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  const newBudgetFooter = (
    <>
      <div>
        <div className="input-group">
          <span className="input-group-text">Итого</span>
          <FormattedNumberInput
            className="form-control"
            style={{ width: 200 }}
            value={totalSum}
            onChange={(e) => undefined}
            disabled
          />
        </div>
      </div>

      {budgetId && isAdmin && (
        <button
          className="btn btn-ghost-danger d-flex gap-1"
          data-bs-toggle="modal"
          data-bs-target="#modal-delete-budget"
          disabled={isFormLoading}
        >
          Удалить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {!isAdmin && budgetId ? undefined : (
        <button type="submit" form="budgetForm" className="btn btn-success d-flex gap-1" disabled={isFormLoading}>
          {budgetId ? "Сохранить изменения" : "Создать бюджет"}
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}
    </>
  );

  return (
    <>
      <ModalComponent
        header={budgetId ? "Детали бюджета" : "Новый бюджет"}
        body={newBudgetBody}
        footer={newBudgetFooter}
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted}
      />
      <DangerModal
        modal_id="modal-delete-budget"
        dangerDescription={deleteBudgetDanger}
        cancel={openBudgetModal}
        confirm={handleBudgetDelete}
      />
    </>
  );
}
